import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Switch,
  Button,
  Typography,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, CircularProgress,
  Paper, Alert, OutlinedInput, ListItemText, Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {
  fetchMonitoringConfigsAPI,
  saveMonitoringConfigAPI,
  updateMonitoringConfigAPI,
} from '../operations/monitoringService';

// -------------------- Styled Switch --------------------
const BlackWhiteSwitches = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#000',
    '& + .MuiSwitch-track': {
      backgroundColor: '#000',
    },
  },
  '& .MuiSwitch-switchBase': {
    color: '#ccc',
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#ccc',
  },
}));

// -------------------- Types --------------------
// interface Supplier {
//   id: number;
//   name: string;
// }

// interface SourceSuppliers {
//   [source: string]: Supplier[];
// }

interface MonitoringConfig {
  id: number; // make sure backend returns unique id
  product: string;
  supplierName: string;
  supplierSource: string;
  country: string;
  city: string;
  noOfRecords: number;
  recordThreshold: number;
  responseThreshold: number;
  isActive: boolean;
  supplierId?: number;
}

const StayMonitoringSetting: React.FC = () => {
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [alertSeverity, setAlertSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('error'); 
   // form/edit states
    const [isEdit, setIsEdit] = useState(false);
    const [editingRecord, setEditingRecord] = useState<MonitoringConfig | null>(null);
    const [monitoringData, setMonitoringData] = useState<MonitoringConfig[]>([]);
    const [loading, setLoading] = useState(false);

    const [responseMessage, setResponseMessage] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const handleCloseModal = () => setOpenModal(false);

    // const [supplierName, setSupplierName] = useState<string[]>(["Nuitee", "Hotel Trader"]);

    // Supplier mapping
const supplierNamesMap: Record<string, string[]> = {
  API: ["Nuitee", "Hotel Trader"],
};

// Selected supplier(s)
const [supplierName, setSupplierName] = useState<string>("");


// Source → Supplier list
interface SupplierOption {
  id: number;
  name: string;
}

const sourcesMap: Record<string, SupplierOption[]> = {
  API: [
    { id: 0, name: "Nuitee" },
    { id: 1, name: "Hotel Trader" },
  ],
};

const [supplierDetails, setSupplierDetails] = useState<
    { supplierName: string; supplierId: number }[]
  >([]);


    // const [supplierName, setSupplierName] = useState<string[]>([]);
    const [formData, setFormData] = useState<{
      country: string | number;
      city: string | number;
      noOfRecords: string | number;
      responseThreshold: string | number;
      recordThreshold: string | number;
    }>({
      country: '',
      city: '',
      noOfRecords: '',
      responseThreshold: '',
      recordThreshold: '',
    });

    // status/toggle
    const [isActive, setIsActive] = useState<boolean>(true);
    const [toggleChanged, setToggleChanged] = useState<boolean>(false);
  
    // pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const hasNumber = (value: string) => /\d/.test(value);
    const toCapitalized = (value: string) => {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


    // -------------------- Inputs --------------------
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = e.target;
      let value = e.target.value;

      // ---------- COUNTRY CODE SPECIAL RULE ----------
  if (name === "country") {
    // Convert lowercase to UPPERCASE
    value = value.toUpperCase();

    // Limit to 3 characters
    if (value.length > 2) {
      value = value.slice(0, 2);
    }

    // Prevent numbers in country code
    if (/\d/.test(value)) {
      setInvalidFields((prev) => (prev.includes("country") ? prev : [...prev, "country"]));
      setResponseMessage("Country code cannot contain numbers.");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      return;
    }

    // Remove error if value becomes valid
    setInvalidFields((prev) => prev.filter((f) => f !== "country"));
  }

      // Validation: City & Country should NOT contain numbers
      if (name === "city") {
    value = toCapitalized(value);

    // Prevent numbers in City
    if (/\d/.test(value)) {
      setInvalidFields((prev) =>
        prev.includes("city") ? prev : [...prev, "city"]
      );
      setResponseMessage("City cannot contain numbers.");
      setAlertSeverity("error");
      setShowAlert(true);
      return;
    }

    // Remove error if valid
    setInvalidFields((prev) => prev.filter((f) => f !== "city"));
  }

      const numericFields = ['noOfRecords', 'responseThreshold', 'recordThreshold'];
      setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name)
      ? value === "" 
        ? ""          
        : Number(value)
      : value,
  }));
  if (value !== "") setInvalidFields((prev) => prev.filter((f) => f !== name));
    };

   // Handle supplier change
  const handleSupplierNameChange = (event: any) => {
    // setSupplierName(event.target.value);
    const selected = event.target.value;
    setSupplierName(selected);
    if (selected) {
    setInvalidFields((prev) => prev.filter((f) => f !== "Supplier Name"));
  }
  };

 


    // -------------------- Toggle (tracks user action) --------------------
    
    const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsActive(event.target.checked);
      if (isEdit) setToggleChanged(true);
    };

    // -------------------- Save / Update --------------------
    const handleSave = async () => {
      console.log("handleSave called");

    const isEmpty = (val: any) => val === "" || val === null || val === undefined;

     // Map of field names to user-friendly labels
    const fieldLabels: { [k in keyof typeof formData]: string } = {
      country: "Country",
      city: "City",
      noOfRecords: "No. of Records",
      responseThreshold: "Response Time (T.H)",
      recordThreshold: "No. of Records (T.H)",
    };

    const missingFields: string[] = [];

    // Check formData fields
    (Object.keys(formData) as (keyof typeof formData)[]).forEach((field) => {
      if (isEmpty(formData[field])) {
        missingFields.push(field);
      }
    });

    // Also check supplier selections
    if (!supplierName.length) missingFields.push("Supplier Name");

     if (missingFields.length >= 1) {
      if (missingFields.length === 1) {
        setInvalidFields(missingFields);
        setResponseMessage(
          isEdit
            ? `${fieldLabels[missingFields[0] as keyof typeof fieldLabels] || missingFields[0]} is missing. Please update the valid input field.`
            : `${fieldLabels[missingFields[0] as keyof typeof fieldLabels] || missingFields[0]} is missing. Please enter the valid input field.`
        );
        setAlertSeverity("error");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      } else {
      setInvalidFields(missingFields);
      setResponseMessage(
      isEdit
        ? `Please update all valid input fields.`
        : `Please enter all required fields.`
      );
    setAlertSeverity("error");
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  }
  setShowAlert(true);
      return;
    }
  setInvalidFields([]);

  // -------- City/Country should not contain numbers ----------
  const hasNumber = (value: string) => /\d/.test(value);

  if (hasNumber(formData.city.toString())) {
    setInvalidFields(["city"]);
    setResponseMessage("City cannot contain numbers.");
    setAlertSeverity("error");
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
    return;
  }

  if (hasNumber(formData.country.toString())) {
    setInvalidFields(["country"]);
    setResponseMessage("Country cannot contain numbers.");
    setAlertSeverity("error");
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
    return;
  }

  const numericFields: (keyof typeof formData)[] = [
  // "frequency",
  "noOfRecords",
  "responseThreshold",
  "recordThreshold",
];

const invalidNumericFields = numericFields.filter(
  (field) => Number(formData[field]) <= 0
);

if (invalidNumericFields.length > 0) {
  // Highlight all invalid fields
  setInvalidFields(invalidNumericFields);

  // Create a readable list of labels
  const invalidLabels = invalidNumericFields
    .map((f) => fieldLabels[f as keyof typeof fieldLabels] || f)
    .join(", ");

  // Build message
  const message =
    invalidNumericFields.length > 1
      ? `${invalidLabels} must be greater than zero.`
      : `${invalidLabels} must be greater than zero.`;

  setResponseMessage(message);
  setAlertSeverity("error");
  setShowAlert(true);
  setTimeout(() => setShowAlert(false), 5000);
  return; // stop save
}

 // Check for duplicates
  const latestSupplierDetails = [
  {
    supplierSource: "API",
    supplierName: supplierName,
  },
];

  const duplicate = latestSupplierDetails.some((detail) =>
    monitoringData.some(
      (existing) =>
        existing.supplierSource === detail.supplierSource &&
        existing.supplierName === detail.supplierName
    )
  );

  if (duplicate && !isEdit) {
    setResponseMessage("This configuration already exists.");
    setAlertSeverity("error");
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
    return;
  }

      
  
      let payload: any = {};
  
      if (isEdit && editingRecord) {
        // --- UPDATE: only changed fields + mandatory identifiers ---
        payload = {
          id: editingRecord.id,   // <-- Important!
          product: 'Hotel',
          supplierId: editingRecord.supplierId ?? 0,
          supplierName: editingRecord.supplierName,
        };
  
        let changed = false;
  
        // compare fields (excluding isActive)
        const fieldsToCheck: (keyof typeof formData)[] = [
          'country',
          'city',
          'noOfRecords',
          'responseThreshold',
          'recordThreshold',
        ];
  
        fieldsToCheck.forEach((field) => {
          const newValueAny = formData[field];
          const oldValueAny = (editingRecord as any)[field];
  
          // normalize (string vs number)
          let newValue = newValueAny;
          let oldValue = oldValueAny;
  
          if (
            typeof oldValue === 'number' &&
            typeof newValue === 'string' &&
            !isNaN(Number(newValue))
          ) {
            newValue = Number(newValue);
          }
  
          if (newValue !== oldValue) {
            payload[field] = newValue;
            changed = true;
          }
        });
  
        // include isActive ONLY if the user actually toggled
        if (toggleChanged) {
          payload.isActive = isActive;
          changed = true;
        }
  
        if (!changed) {
          console.log('No changes detected. Skipping update.');
          setResponseMessage('No changes detected.');
          setAlertSeverity("info");
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 5000);
          setLoading(false);
          return;
        }
      } else {
        // --- CREATE: full payload ---
        
        const supplierDetailsPayload = [{
          supplierId: sourcesMap["API"].find((s) => s.name === supplierName)?.id ?? 0,
          supplierSource: "API",
          supplierName: supplierName,
        }];
        payload = {
          product: 'Hotel',
          supplierDetails: supplierDetailsPayload,
          country: formData.country.toString().trim(),
          city: formData.city.toString().trim(),
          noOfRecords: Number(formData.noOfRecords),
          responseThreshold: Number(formData.responseThreshold),
          recordThreshold: Number(formData.recordThreshold),
          isActive,
        };
      }

  
      setLoading(true);
      try {
        console.log('Sending payload:', payload);
        if (isEdit) {
          await updateMonitoringConfigAPI(payload);
        } else {
          await saveMonitoringConfigAPI(payload);
        }
        await fetchMonitoringConfigs();
        resetForm();
        setResponseMessage(isEdit ? 'Configuration updated successfully.' : 'Configuration saved successfully.');
        setAlertSeverity("success");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      } catch (error) {
        console.error('Error saving data:', error);
      setResponseMessage('Unable to save the data. Please try again.');
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
    } finally {
      setLoading(false);
    }
  };
  
    // -------------------- Reset / Fetch / Edit --------------------
    const resetForm = () => {
      setSupplierName("");
      setFormData({
        country: '',
        city: '',
        noOfRecords: '',
        responseThreshold: '',
        recordThreshold: '',
      });
  
      // default UI state
      setIsActive(true);
      setToggleChanged(false);
      setShowAlert(false);
      setIsEdit(false);
      setEditingRecord(null);
    };
  
    const fetchMonitoringConfigs = async () => {
      try {
        setLoading(true);
        const response = await fetchMonitoringConfigsAPI({ allRecords: true, product: 'Hotel' });
        const rows = response?.data?.data || [];
        setMonitoringData(rows);
      } catch (err) {
        console.error('Failed to fetch monitoring configs:', err);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchMonitoringConfigs();
    }, []);
  
    const handleEdit = (row: MonitoringConfig) => {
      setIsEdit(true);
      setInvalidFields([]);
      setEditingRecord(row);
      setSupplierName(row.supplierName || "");
      setFormData({
        country: row.country ?? '',
        city: row.city ?? '',
        noOfRecords: row.noOfRecords ?? '',
        responseThreshold: row.responseThreshold ?? '',
        recordThreshold: row.recordThreshold ?? '',
      });
      setIsActive(Boolean(row.isActive));
      setToggleChanged(false); // reset when entering edit
    };

    const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };
  
    const paginatedRows = monitoringData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    

 
  return (
    <>
    {showAlert && (
        <Box className="alertWrap">
          <Alert
            severity={alertSeverity} // dynamic severity
            onClose={() => setShowAlert(false)}
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setShowAlert(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {responseMessage}
          </Alert>
        </Box>
      )}
{/* <Dialog open={openModal} onClose={handleCloseModal}>
  <DialogTitle>Notification</DialogTitle>
  <DialogContent>
    <Typography>{responseMessage}</Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseModal} variant="contained" color="primary">
      OK
    </Button>
  </DialogActions>
</Dialog> */}
        <Box className="headingBtnWrap">
            <Typography variant="h1">Stay Monitoring Setting</Typography>
        </Box>
         <Box className="feeInputFieldWrap horizontalWrap w44">
            <div className="form-control inputFieldWrap">
              <label>Supplier Source</label>
              <input
                type="text"
                name="supplierSource"
                className="inputField"
                value="API"
                readOnly
              />
            </div>
            <div className="form-control inputFieldWrap">
            <label>Supplier Name</label>
<FormControl
  sx={{ minWidth: 270 }}
  className={`commondropdownWrap ${
    invalidFields.includes("Supplier Name") ? "redBorder" : ""
  } ${isEdit ? "pointerEvents" : ""}`}
>
  <Select
    id="supplier-select"
    className="commondropdown"
    value={supplierName} // single-select
    onChange={(event) => {
      const selected = event.target.value;
      setSupplierName(selected);
      if (selected) {
  setInvalidFields((prev) => prev.filter((f) => f !== "Supplier Name"));
}
    }}
    displayEmpty
    renderValue={(selected) => {
      if (!selected) {
        return <span style={{ color: "#888" }}>Select Supplier Name</span>;
      }
      return selected;
    }}
  >
    {/* <MenuItem disabled value="">
      <em>Select Supplier Name</em>
    </MenuItem> */}

    {sourcesMap["API"].map((supplier) => (
      <MenuItem key={supplier.id} value={supplier.name}>
        {supplier.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>
</div>
            

            
<div className="form-control inputFieldWrap">
  <label>Country Code</label>
  <input
    type="text"
    name="country"
    className={`inputField ${
    invalidFields.includes("country") ? "redBorder" : ""
  }`}
    value={formData.country}
    onChange={handleInputChange}
  />
</div>
        <div className="form-control inputFieldWrap">
  <label>City</label>
  <input
    type="text"
    name="city"
    className={`inputField ${
    invalidFields.includes("city") ? "redBorder" : ""
  }`}
    value={formData.city}
    onChange={handleInputChange}
  />
</div>
        <div className="form-control inputFieldWrap">
  <label>No. of Records</label>
  <input
    type="number"
    name="noOfRecords"
    className={`inputField ${
    invalidFields.includes("noOfRecords") ? "redBorder" : ""
  }`}
    value={formData.noOfRecords}
    onChange={handleInputChange}
  />
</div>

<div className="form-control inputFieldWrap">
  <label>Response Time (T.H)</label>
  <input
    type="number"
    name="responseThreshold"
    className={`inputField ${
    invalidFields.includes("responseThreshold") ? "redBorder" : ""
  }`}
    value={formData.responseThreshold}
    onChange={handleInputChange}
  />
</div>

<div className="form-control inputFieldWrap">
  <label>No. of Records (T.H)</label>
  <input
    type="number"
    name="recordThreshold"
    className={`inputField ${
    invalidFields.includes("recordThreshold") ? "redBorder" : ""
  }`}
    value={formData.recordThreshold}
    onChange={handleInputChange}
  />
</div>
        {/* Status */}
                <div className="form-control inputFieldWrap statusRevert">
                  <label>Status</label>
                  <Box display="flex" alignItems="center" gap={2} className={`${isEdit ? "" : "pointerEvents"}`}>
                    <BlackWhiteSwitches checked={isActive} onChange={handleToggle} />
                  </Box>
                </div>
                {/* Health */}
            <div className="form-control inputFieldWrap healthWrap">
                <label>Health</label>
                 <Accordion className='healthAccordian'>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
            <Box className="healthHeaderBox">
                        <Box>0 to 3 sec</Box>
                        <Box>Green</Box>
                    </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="healthBoxWrap">
                    <Box className="healthBox">
                        <Box>4 to 5 sec</Box>
                        <Box>Yellow</Box>
                    </Box>
                    <Box className="healthBox">
                        <Box>6 to 7 sec</Box>
                        <Box>Orange</Box>
                    </Box>
                    <Box className="healthBox">
                        <Box>8 to 9 sec</Box>
                        <Box>Red</Box>
                    </Box>
                </Box>
        </AccordionDetails>
      </Accordion>
            </div>
          <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button variant="contained" className="formBtn" onClick={handleSave} disabled={loading}
          style={{ backgroundColor: '#272727', color: '#fff', padding: '8px 14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {loading && (
                          <CircularProgress
                            size={20}
                            thickness={5}
                            sx={{ color: '#fff' }}
                          />
                        )}
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </Box>
        </Box>
        {/* Table */}
             <Box mt={4}>
               <Paper sx={{ width: '100%', overflowX: 'auto' }}>
                 <TableContainer sx={{ maxHeight: 440 }}>
                   <Table stickyHeader>
                     <TableHead>
                       <TableRow>
                         <TableCell>Supplier Source</TableCell>
                         <TableCell align="center">Supplier Name </TableCell>
                         <TableCell align="center">Country Code</TableCell>
                         <TableCell align="center">City</TableCell>
                         <TableCell align="center">No. of Records</TableCell>
                         <TableCell align="center">Response Time (T.H)</TableCell>
                         <TableCell align="center">No. of Records (T.H)</TableCell>
                         <TableCell align="center">Status</TableCell>
                         <TableCell align="center">Action</TableCell>
                       </TableRow>
                     </TableHead>
                     <TableBody>
                       {loading ? (
                         <TableRow>
                           <TableCell colSpan={9} align="center">
                             Loading...
                           </TableCell>
                         </TableRow>
                       ) : paginatedRows.length === 0 ? (
                         <TableRow>
                           <TableCell colSpan={9} align="center">
                             No records found.
                           </TableCell>
                         </TableRow>
                       ) : (
                         paginatedRows.map((row, index) => (
                           <TableRow key={index}>
                             <TableCell>{row.supplierSource}</TableCell>
                             <TableCell align="center">{row.supplierName}</TableCell>
                             <TableCell align="center">{row.country}</TableCell>
                             <TableCell align="center">{row.city}</TableCell>
                             <TableCell align="center">{row.noOfRecords}</TableCell>
                             <TableCell align="center">{row.responseThreshold}</TableCell>
                             <TableCell align="center">{row.recordThreshold}</TableCell>
                             <TableCell align="center">
                               <Typography color={row.isActive ? 'green' : 'gray'}>
                                 {row.isActive ? 'Active' : 'Inactive'}
                               </Typography>
                             </TableCell>
                             <TableCell align="center">
                               <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                                 <EditIcon />
                               </IconButton>
                             </TableCell>
                           </TableRow>
                         ))
                       )}
                     </TableBody>
                   </Table>
                 </TableContainer>
       
                 <TablePagination
                   rowsPerPageOptions={[10, 25, 50]}
                   component="div"
                   count={monitoringData.length}
                   rowsPerPage={rowsPerPage}
                   page={page}
                   onPageChange={handleChangePage}
                   onRowsPerPageChange={handleChangeRowsPerPage}
                 />
               </Paper>
             </Box>
    </>
  )
}

export default StayMonitoringSetting
